import React from 'react';
import _ from 'lodash';
import { InfoCircleOutlined } from '@ant-design/icons';
import Tooltip from 'rc-tooltip';
import Axios from '../request';
import FormConsumer from '../collect-info/FormConsumers';
import {
  Flex, Text, ActionNote, Box,
} from '../components';
import { Button } from '../styles';
import * as S from '../styles';
import ReviewStep from './ReviewStep';
import { PageTitle } from '../biz-components/page-title';
import splitHomeAddressQuestion from './splitHomeAddressQuestion';

class FillFormAction extends React.Component {
  handleQuestionChange({ question, value }) {
    const questions = this.state.questions.map((q) => {
      if (q.key !== question.key) return q;

      const answer = {
        ...q.answer,
        answer: {
          ...q.answer.answer,
          [q.id]: value,
        },
      };

      const updatedQuestion = {
        ...q,
        answer,
      };
      return updatedQuestion;
    });

    this.setState({ questions });
  }

  handleEmployeeSubmitForm() {
    const answersPayload = this.answersPayloadFromQuestions();
    const { emergencyContactConsent } = this.state;

    const payload = {
      employee_information: {
        ...answersPayload,
        emergency_contact_consent: emergencyContactConsent,
      },
    };

    const { action: { action_type: actionType, digest_key: actionDigestKey } } = this.props;
    const { action: { employee_information: { digest_key: eiDigestKey } } } = this.props;
    const { authCode } = this.props;
    const apiPath = actionType === 'hr_fill_basic_information_form' ? 'hr' : 'employee';

    const config = {
      headers: {
        ...(actionType === 'hr_fill_basic_information_form' && { Authorization: authCode }),
      },
      'Access-Control-Allow-Origin': '*',
    };

    this.setState({ isSubmitting: true, saveDataError: '' });

    const submitApiURL = `${APIHOST}/v1/onboarding/${apiPath}/employee_informations/:digest_key`;
    const completeApiURL = `${APIHOST}/v1/onboarding/employee/employee_onboarding_actions/:digest_key/completed`;
    Axios.put(submitApiURL.replace(':digest_key', eiDigestKey), payload, config)
      .then((data) => {
        Axios.put(completeApiURL.replace(':digest_key', actionDigestKey), {}, config)
          .then(() => {
            this.setState({ isSubmitting: false });
            if (this.props.onSubmitFormSuccess) {
              this.props.onSubmitFormSuccess();
            }
          })
          .catch((error) => {
            let saveDataError = 'Something went wrong while saving data.';
            if (error && error.response && error.response.data) {
              const { error_code: errorCode, message } = error.response.data;
              if (message) {
                saveDataError = message;
              }
              if (errorCode === 43001 || errorCode === 43002) {
                // Need to refresh the side bar action status
                if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
              }
            }
            this.setState({ isSubmitting: false, saveDataError });
          });
      })
      .catch((error) => {
        let saveDataError = 'Something went wrong while saving data.';
        if (error && error.response && error.response.data) {
          const { error_code: errorCode, message } = error.response.data;
          if (message) {
            saveDataError = message;
          }
          if (errorCode === 43001 || errorCode === 43002) {
            // Need to refresh the side bar action status
            if (this.props.refreshProgressDataFn) this.props.refreshProgressDataFn();
          }
          if (errorCode === 40000 && message) {
            const errorMessages = Object.keys(message).map((key) => {
              const mappedMessage = message[key].map((msg) => `${key} ${msg}`);
              return mappedMessage;
            });
            saveDataError = errorMessages;
            this.handleReviewState();
          }
        }
        this.handleReviewState();
        this.setState({ isSubmitting: false, saveDataError });
      });
  }

  questionsWithAnswers(questions, prefilledData, ei) {
    return _.sortBy(splitHomeAddressQuestion(questions), 'sequence').map((question) => {
      const answer = {
        answer: {
          [question.key]: ei[question.key] || prefilledData[question.key] || '',
        },
      };

      return {
        ...question,
        id: question.key,
        answer,
      };
    });
  }

  answersPayloadFromQuestions() {
    const { questions = [] } = this.state;

    return questions.reduce(
      (acc, question) => {
        const { id: questionId } = question;
        const { answer } = question.answer;

        if (question.question_type === 'date') {
          // Vincent: here we convert 'LL' to 'YYYY-MM-DD' but 'LL' format won't be supported in next moment major update...
          // Refer to: http://momentjs.com/guides/#/warnings/js-date/
          const date = moment(answer[questionId]);
          answer[questionId] = date.isValid() ? moment(answer[questionId]).format('YYYY-MM-DD') : '';
        }
        acc[questionId] = answer[questionId];

        return acc;
      },
      {},
    );
  }

  handleEmployeePressNext() {
    // Check for errors
    if (this.formConsumer && this.formConsumer.current) {
      this.formConsumer.current.validate((error) => {
        if (error) return;
        // if no errors, direct to the next review page
        this.setState({ reviewState: true });
      });
    }
  }

  renderNextButtons() {
    let disabled = false;
    let disableNote = '';
    const { actionStatus, saveDataError } = this.state;
    const { action: { action_type: actionType } } = this.props;
    const isEmployeeForm = actionType === 'employee_fill_basic_information_form';

    if (this.props.previewMode) {
      disabled = true;
      disableNote = 'Cannot submit form in preview mode.';
    } else if (actionStatus !== 'not_started' && actionStatus !== 'in_progress' && actionStatus !== 'redo') {
      disabled = true;
      disableNote = 'Form has been submitted.';
    } else if (isEmployeeForm && companyStaff && companyStaff.id) {
      disabled = true;
      disableNote = 'This form should be submitted by employee.';
    }

    return (
      <Flex column alignItems="flex-end">
        <Button
          disabled={disabled}
          onClick={this.handleEmployeePressNext.bind(this)}
          data-testid="basic-info-next-btn"
        >
          Next
        </Button>
        {disabled && <ActionNote text={disableNote} />}
        {saveDataError && (
        <Flex justifyContent="flex-end" mt={1}>
          {Array.isArray(saveDataError)
            ? <ul>{saveDataError.map((error) => <li><Text color="red">{error}</Text></li>)}</ul>
            : <Text color="red">{saveDataError}</Text>}
        </Flex>
        )}
      </Flex>
    );
  }

  renderSubmitText() {
    const { actionStatus } = this.state;
    let buttonText = 'Submit';
    if (actionStatus === 'in_progress' || actionStatus === 'redo') {
      buttonText = 'Re-submit';
    }
    if (this.state.isSubmitting) {
      buttonText += 'ting';
    }
    return buttonText;
  }

  // it will trigger the reviewState to be false
  handleReviewState() {
    this.setState({ reviewState: false });
  }

  render() {
    const { questions, emergencyContactConsent, isEmployeeAction } = this.state;

    this.isOnboardingV2 = isOnboardingV2 === 'true';

    return (
      <Flex column>
        {this.state.reviewState
          ? (
            <Flex column style={{ maxWidth: '675px', margin: 'auto' }}>
              <ReviewStep
                questions={questions}
                onReviewStateHandler={this.handleReviewState.bind(this)}
                onSubmitHandler={this.handleEmployeeSubmitForm.bind(this)}
                renderSubmitText={this.renderSubmitText.bind(this)}
                emergencyContactConsent={emergencyContactConsent}
                loading={this.state.isSubmitting}
              />
            </Flex>
          )
          : (
            <>
              {
                this.isOnboardingV2 && <PageTitle title={this.props.action.module_name} />
              }
              <Flex column style={this.isOnboardingV2 ? { paddingLeft: '4px', paddingRight: '4px' } : { width: '310px', margin: 'auto' }}>
                {
                  !this.isOnboardingV2
                    && <Flex mb={20}><Text fontSize="22px" bold>{this.props.title}</Text></Flex>
                }
                <FormConsumer
                  questions={questions}
                  onQuestionChange={this.handleQuestionChange.bind(this)}
                  ref={this.formConsumer}
                  {...this.props.viewResultAsManager && { reviewActions: [] }}
                />
                {
                  isEmployeeAction && (
                    <Flex column>
                      <S.QuestionWrapper mb="20px">
                        <Flex>
                          <label>
                            <Flex>
                              <S.Input
                                style={{ height: '20px' }}
                                type="checkbox"
                                value={emergencyContactConsent}
                                onChange={(e) => this.setState({ emergencyContactConsent: e.target.checked })}
                                checked={emergencyContactConsent}
                              />
                              <Box ml={1} />
                              I consent to my employer notifying my emergency contact if I
                              am arrested or detained by law enforcement.
                            </Flex>
                          </label>
                          <Tooltip
                            overlay={(
                              <Box width={280} p="8px">
                                This allows your employer to contact your emergency contact if needed in urgent situations, including if you are detained or arrested at work.
                              </Box>
                            )}
                            placement="top"
                            trigger={['hover', 'click']}
                          >
                            <InfoCircleOutlined style={{ cursor: 'pointer', marginTop: '5px' }} />
                          </Tooltip>
                        </Flex>
                      </S.QuestionWrapper>
                    </Flex>
                  )
                }
                {this.renderNextButtons()}
              </Flex>
            </>
          )}
      </Flex>
    );
  }
}

export default FillFormAction;
